import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEO from '../components/SEO';

// Icons
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const VideoIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m22 8-6 4 6 4V8Z"/>
    <rect width="14" height="12" x="2" y="6" rx="2" ry="2"/>
  </svg>
);

const PlayIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const VideographerQuotingSoftware = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  useEffect(() => {
    analytics.track('Videographer Landing Page View');
  }, []);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        // Scrolling up or near top - show header
        setIsVisible(true);
      } else {
        // Scrolling down - hide header
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Videographer Quote & Quotation Software - Interactive Proposals | QuoteTier"
        description="The best videographer quotation software. transform flat quotes into interactive proposals that let clients choose production upgrades."
        canonical="/videographer-quote-software"
        ogTitle="Videographer Quote Software That Increases Deal Size"
        ogDescription="Interactive videography proposals that let clients upsell themselves. Transform flat quotes into engaging, mobile-optimized experiences."
        noindex={true}
      />
      {/* Header */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'navigation', action: 'signup' })}>
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'mobile-navigation', action: 'signup' })}>
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-32 md:py-40 lg:py-48">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
                Videographer Quotes That<br />
                <span className="text-primary font-bold">Sell Cinematic Upgrades</span>
              </h1>
              <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
                Stop losing money on basic video quotes. QuoteTier's interactive proposals let clients choose drone footage, multi-camera setups, and premium editing—turning $1,500 projects into $5,000+ cinematic experiences automatically.
              </p>
              <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center mb-6">
                <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'videographer-hero', action: 'signup' })}>
                  <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                    Start Free — 3 Quotes/Mo
                  </Button>
                </Link>
                <Link to="/demo-quote/videography" onClick={() => analytics.track('Demo Clicked', { location: 'videographer-hero', industry: 'videography' })}>
                  <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" variant="outline">
                    See Video Quote Demo →
                  </Button>
                </Link>
              </div>
              <p className="font-jetbrains mt-10 text-lg text-gray-500">
                No credit card required • Setup in 5 minutes • Perfect for freelancers & studios
              </p>
            </div>
          </div>
        </section>

        {/* Video Production Quote Example */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                See How Clients Choose Higher-Value Video Packages
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                Instead of one flat price, clients see three production levels and naturally upgrade to premium features
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
              {/* Basic Package */}
              <Card className="relative border border-black transition-all">
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Basic Video Package</CardTitle>
                    <span className="text-2xl font-bold">$1,500</span>
                  </div>
                  <CardDescription className="font-jetbrains">Essential video coverage and editing</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />4 hours of filming</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Basic editing & color correction</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Background music</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Digital delivery (MP4)</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />One revision round</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Popular Package */}
              <Card className="relative border border-black transform scale-105">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold">Most Popular</span>
                </div>
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Professional Video Production</CardTitle>
                    <span className="text-2xl font-bold">$3,000</span>
                  </div>
                  <CardDescription className="font-jetbrains">Complete video production with enhanced features</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />8 hours of filming</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Professional editing & color grading</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Custom graphics & titles</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Drone footage included</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Premium music licensing</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Multiple format delivery</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Three revision rounds</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Social media versions</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Premium Package */}
              <Card className="relative border border-black">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold">Best Value</span>
                </div>
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Cinematic Video Experience</CardTitle>
                    <span className="text-2xl font-bold">$5,000</span>
                  </div>
                  <CardDescription className="font-jetbrains">Premium video production with full post-production</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />12+ hours of filming</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Cinematic editing & advanced color grading</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Custom motion graphics & animations</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Multi-camera setup</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Professional audio recording</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Same-day highlight reel</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Full raw footage delivery</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Unlimited revisions</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Custom branded delivery portal</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />1-year cloud storage</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-12">
              <Link to="/demo-quote/videography" onClick={() => analytics.track('Demo Clicked', { location: 'videographer-pricing', industry: 'videography' })}>
                <Button className="px-8 py-4 text-lg font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Try This Interactive Video Quote →
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Why Videographers Choose QuoteTier */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                Why Professional Videographers Choose QuoteTier
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                The only quoting software designed for videographers who want to showcase their premium production value
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <VideoIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-xl">Pre-Built Video Packages</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains text-sm">Ready-to-use Good/Better/Best video production packages with all the premium features clients want - from basic edits to cinematic masterpieces.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <PlayIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-xl">Visual Portfolio Integration</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains text-sm">Showcase your work visually within quotes - let your stunning videography sell your premium packages for you.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <ZapIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-xl">Real-Time Client Engagement</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains text-sm">Know exactly when clients open your quote, how long they spend reviewing each package, and which features excite them most.</p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 bg-white border border-black rounded-2xl p-8 text-center">
              <h3 className="font-instrument text-2xl font-bold mb-4">Perfect for All Video Professionals</h3>
              <p className="font-jetbrains text-gray-700 mb-6 max-w-3xl mx-auto">
                Whether you shoot weddings, corporate videos, commercials, or creative content, QuoteTier helps you present your services with the same visual excellence as your work. Stop competing on price - let your quality and options sell themselves.
              </p>
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'videographer-perfect-for', action: 'signup' })}>
                <Button className="px-8 py-3 text-lg font-semibold bg-black text-white hover:bg-gray-800 rounded-lg">
                  Start Free
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                QuoteTier vs Traditional Video Proposals
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                See why smart videographers are switching from email quotes to interactive proposals
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">
                      Feature
                    </th>
                    <th className="text-center py-6 px-6 bg-black text-white">
                      <div className="flex flex-col items-center gap-2">
                        <span className="font-instrument text-lg font-bold">QuoteTier</span>
                        <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full">
                          Video Optimized
                        </span>
                      </div>
                    </th>
                    <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">
                      Email Quotes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Client can choose production level</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Automatic upselling to cinematic packages</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Track client engagement with quote</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Beautiful visual presentation</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Digital contract signing</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Time to create quote</td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains font-semibold text-gray-800 bg-gray-100 px-3 py-1 rounded-full text-sm">
                        5 minutes
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains text-gray-600 text-sm">30+ minutes</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                Videographers Increase Project Value by 80%+ with QuoteTier
              </h2>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Average Project Increase</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-2">+$1,800</div>
                  <p className="font-jetbrains text-sm text-muted-foreground">Clients naturally choose premium packages when they can clearly see the production value difference.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Quote Acceptance Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-2">88%</div>
                  <p className="font-jetbrains text-sm text-muted-foreground">Interactive video quotes get accepted faster than traditional email proposals.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Time Saved Per Quote</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-2">25+ min</div>
                  <p className="font-jetbrains text-sm text-muted-foreground">No more writing lengthy email proposals - use templates and customize with your style.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-gray-900 to-black text-white">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-instrument text-4xl md:text-5xl font-bold mb-6">
              Ready to Sell More Cinematic Video Production?
            </h2>
            <p className="font-jetbrains text-xl text-gray-200 mb-8">
              Join hundreds of videographers using QuoteTier to automatically upsell clients to premium production packages.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'videographer-final-cta', action: 'signup' })}>
                <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-100 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <Link to="/demo-quote/videography" onClick={() => analytics.track('Demo Clicked', { location: 'videographer-final-cta', industry: 'videography' })}>
                <Button className="px-8 py-4 text-lg font-semibold border-2 border-white text-white hover:bg-white/10 rounded-lg" variant="outline">
                  Try Interactive Demo First →
                </Button>
              </Link>
            </div>
            
            <p className="font-jetbrains text-sm text-gray-400">
              No credit card required • Cancel anytime • Setup in minutes
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t">
        <div className="container max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground">&copy; 2025 QuoteTier. All rights reserved. <span className="footer-credit">Site by <a href="https://novanorthstudio.com/?utm_source=quotetier.com&utm_medium=referral&utm_campaign=site-credit">Nova North Studio</a></span></p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
            <a href="mailto:info@quotetier.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VideographerQuotingSoftware;
